import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  formatCompactNumber,
  formatMicroUsd,
  formatPercent,
} from '@/components/admin/AssistantConfigurationPage/usage/usageFormatting';
import { toInsightsFilters } from '@/components/admin/AssistantConfigurationPage/usage/usageFilters';
import { Card } from '@/components/ui/card';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useAssistantInsightsSummaryQuery } from '@/hooks/useAssistantInsights';

interface AssistantUsageStripProps {
  tenantId: string;
  tenantName: string;
}

export function AssistantUsageStrip({ tenantId, tenantName }: AssistantUsageStripProps) {
  const { t, i18n } = useTranslation('assistant');
  const language = i18n.language;
  const today = useMemo(function captureToday() {
    return new Date();
  }, []);
  const filters = toInsightsFilters(
    {
      period: 'this_month',
      customFrom: '',
      customTo: '',
      tenantId,
      tenantName,
      topic: null,
      screen: null,
    },
    today
  );
  const summaryQuery = useAssistantInsightsSummaryQuery(filters);
  const summary = summaryQuery.data;
  if (summary === undefined) {
    return null;
  }
  const usedTokens = summary.totals.inputTokens + summary.totals.outputTokens;
  const budgetRatio =
    summary.monthlyBudgetTokens > 0 ? usedTokens / summary.monthlyBudgetTokens : 0;
  const detailsSearch = new URLSearchParams({ workspace: tenantId, workspaceName: tenantName });
  const figures: [string, string][] = [
    [t('insights.kpis.cost'), formatMicroUsd(summary.totals.costMicroUsd, language)],
    [
      t('insights.strip.projected'),
      summary.totals.projectedCostMicroUsd === null
        ? '—'
        : formatMicroUsd(summary.totals.projectedCostMicroUsd, language),
    ],
    [t('insights.kpis.questions'), formatCompactNumber(summary.totals.questions, language)],
    [t('insights.strip.budget'), formatPercent(budgetRatio, language)],
  ];
  return (
    <Card padding="sm" className="mb-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-bold text-zooindigo">{t('insights.strip.title')}</h3>
        <Link
          to={APPLICATION_ROUTES.adminAssistant + '?' + detailsSearch.toString()}
          className="text-xs font-semibold text-zooprimary hover:underline"
        >
          {t('insights.strip.details')}
        </Link>
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {figures.map(function renderFigure([label, value]) {
          return (
            <div
              key={label}
              className="rounded-xl bg-zoooffwhite border border-zooborder px-3 py-2"
            >
              <dt className="text-[11px] text-zoomuted">{label}</dt>
              <dd className="text-lg font-extrabold text-zooindigo">{value}</dd>
            </div>
          );
        })}
      </dl>
    </Card>
  );
}
